/**
 * VariablePicker — the one "Insert variable" control for every template editor.
 *
 * Fed by GET /api/template-variables?applies_to=…, so it lists the platform's
 * system variables, this org's own variables and its custom fields, grouped by
 * category and searchable. Choosing one calls `onInsert(key)`; the editor puts
 * `{{key}}` at its cursor (see insertToken).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Braces, ChevronDown, Search } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { AppliesTo, TemplateVariable } from './api';
import { useVariableRegistry } from './useVariableRegistry';
import { categoryLabel, sortCategories } from './labels';

interface VariablePickerProps {
  /** Which surface: messages, forms, or 'all' (letters render every variable). */
  appliesTo: AppliesTo | 'all';
  /** Called with the bare key; the caller wraps it as `{{key}}`. */
  onInsert: (key: string) => void;
  disabled?: boolean;
  /** Keys to leave out (e.g. variables a surface cannot fill). */
  exclude?: string[];
  /** Button label override. */
  label?: string;
  align?: 'left' | 'right';
  className?: string;
  /** Only offer these scopes (default: all). */
  scopes?: TemplateVariable['scope'][];
  /** Extra filter, e.g. only what a public post can resolve. */
  filter?: (v: TemplateVariable) => boolean;
  /** Footer link to the Template variables admin screen (default on). */
  showManageLink?: boolean;
}

export function VariablePicker({
  appliesTo,
  onInsert,
  disabled,
  exclude,
  label,
  align = 'left',
  className = '',
  scopes,
  showManageLink = true,
  filter,
}: VariablePickerProps) {
  const { t } = useAppTranslation();
  const { variables, loading, error } = useVariableRegistry(appliesTo);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const skip = new Set(exclude ?? []);
    const filtered = variables.filter((v) => {
      if (skip.has(v.key)) return false;
      if (scopes && !scopes.includes(v.scope)) return false;
      if (filter && !filter(v)) return false;
      if (!q) return true;
      return (
        v.key.toLowerCase().includes(q) ||
        (v.label || '').toLowerCase().includes(q) ||
        (v.description || '').toLowerCase().includes(q)
      );
    });
    const byCat = new Map<string, TemplateVariable[]>();
    for (const v of filtered) {
      const cat = v.category || 'general';
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat)!.push(v);
    }
    return sortCategories([...byCat.keys()]).map((cat) => ({ cat, items: byCat.get(cat)! }));
  }, [variables, query, exclude, scopes, filter]);

  const choose = (key: string) => {
    onInsert(key);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={rootRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2 disabled:opacity-50"
      >
        <Braces className="h-3.5 w-3.5" aria-hidden />
        {label ?? t('templateVariables.picker.insert', 'Insert variable')}
        <ChevronDown className="h-3 w-3" aria-hidden />
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-1 w-80 max-w-[90vw] rounded-lg border border-v2border bg-surface shadow-lg ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className="flex items-center gap-2 border-b border-v2border px-2.5 py-2">
            <Search className="h-3.5 w-3.5 text-ink-500" aria-hidden />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('templateVariables.picker.search', 'Search variables')}
              aria-label={t('templateVariables.picker.search', 'Search variables')}
              className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-500"
            />
          </div>
          <div role="listbox" className="max-h-72 overflow-y-auto py-1">
            {loading && (
              <p className="px-3 py-2 text-xs text-ink-500">{t('templateVariables.picker.loading', 'Loading variables…')}</p>
            )}
            {!loading && error && (
              <p className="px-3 py-2 text-xs text-red-600">
                {t('templateVariables.picker.loadError', 'Could not load variables. Try again in a moment.')}
              </p>
            )}
            {!loading && !error && groups.length === 0 && (
              <p className="px-3 py-2 text-xs text-ink-500">{t('templateVariables.picker.empty', 'No variables match.')}</p>
            )}
            {groups.map(({ cat, items }) => (
              <div key={cat} className="py-1">
                <p className="px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-500">
                  {categoryLabel(t, cat)}
                </p>
                {items.map((v) => (
                  <button
                    key={`${v.scope}:${v.key}`}
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => choose(v.key)}
                    title={v.description || v.requires || undefined}
                    className="flex w-full items-start justify-between gap-2 px-3 py-1.5 text-left hover:bg-paper-2"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-ink">{v.label || v.key}</span>
                      <span className="block truncate font-mono text-[11px] text-ink-500">{v.placeholder || `{{${v.key}}}`}</span>
                      {v.requires && (
                        <span className="block truncate text-[11px] text-amber-700 dark:text-amber-400">
                          {t('templateVariables.picker.requires', 'Needs {{what}}', { what: v.requires })}
                        </span>
                      )}
                    </span>
                    {v.scope !== 'system' && (
                      <span className="mt-0.5 shrink-0 rounded bg-paper-2 px-1.5 py-0.5 text-[10px] text-ink-500">
                        {v.scope === 'org'
                          ? t('templateVariables.scope.org', 'Organization')
                          : t('templateVariables.scope.customField', 'Custom field')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
          {showManageLink && (
            <div className="border-t border-v2border px-3 py-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  window.dispatchEvent(new CustomEvent('changeTab', { detail: 'template-variables' }));
                }}
                className="text-xs font-medium text-v2accent-ink hover:underline"
              >
                {t('templateVariables.picker.manage', 'Manage template variables')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Insert `{{key}}` at the cursor of an input or textarea (or append when there
 * is none) and return the new text. Restores focus and caret after re-render.
 */
export function insertToken(
  el: HTMLInputElement | HTMLTextAreaElement | null | undefined,
  value: string,
  key: string,
): string {
  const token = `{{${key}}}`;
  if (el && typeof el.selectionStart === 'number') {
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? start;
    const next = value.slice(0, start) + token + value.slice(end);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
    return next;
  }
  return value + token;
}
